import { ApiError, type ClientApi } from '../../lib/api.ts';
import type { Activite, Objectifs } from '../HeuresService.tsx';
import { domainesDu, type Dossier, type Programme } from '../fiches/modele.ts';
import {
  lundisEntre, sectionEleves, sectionEmploiDuTemps, sectionEvaluation, sectionFiches,
  sectionHeures, sectionJournal, sectionProgrammation, sectionProgressions,
  type Bloc, type Creneau, type Eleve, type GrilleEvaluation, type Matiere, type Periode,
  type Progression, type Seance, type Section,
} from './sections.ts';

/**
 * Impressions — aller chercher les données, module par module.
 *
 * Toutes les requêtes portent la classe CHOISIE dans l'écran d'impression,
 * et non la classe de travail : on peut imprimer la classe d'une année
 * passée sans quitter celle de cette année.
 */

/** Les modules imprimables, dans l'ordre du dossier. */
export const MODULES_IMPRIMABLES = [
  { cle: 'eleves', titre: 'Liste des élèves' },
  { cle: 'edt', titre: 'Emploi du temps' },
  { cle: 'programmation', titre: 'Programmation annuelle' },
  { cle: 'progressions', titre: 'Progressions' },
  { cle: 'journal', titre: 'Cahier journal' },
  { cle: 'fiches', titre: 'Fiches de préparation' },
  { cle: 'evaluation', titre: 'Évaluation' },
  { cle: 'heures', titre: '108 heures' },
] as const;

export type CleModule = (typeof MODULES_IMPRIMABLES)[number]['cle'];

export interface Annee {
  id: string;
  label: string;
  startsOn: string;
  endsOn: string;
  active: boolean;
}

export interface Parametres {
  annee: Annee;
  classeId: string;
  /** `null` : toute l'année. */
  periodeId: string | null;
  modules: CleModule[];
  /** Noms des matières retenues pour la programmation, les progressions et l'évaluation. */
  matieres: string[];
  /** Séquences de fiches retenues. */
  sequences: string[];
  unJourParPage: boolean;
}


/**
 * Exécute les tâches quelques-unes à la fois.
 *
 * Quarante semaines de cahier journal en quarante requêtes simultanées
 * déclencheraient la limite de débit de l'API ; une par une, l'attente
 * deviendrait longue. Quatre à la fois est un compromis.
 */
async function parLots<T, R>(elements: T[], tache: (e: T) => Promise<R>, taille = 3): Promise<R[]> {
  const resultats: R[] = [];
  for (let i = 0; i < elements.length; i += taille) {
    resultats.push(...await Promise.all(elements.slice(i, i + taille).map(tache)));
  }
  return resultats;
}

/**
 * Rejoue une requête que le serveur a refusée pour cause d'affluence.
 *
 * Le proxy limite le débit par visiteur : un dossier d'une année envoie
 * encore une trentaine de requêtes, et une autre fenêtre ouverte sur
 * l'application peut suffire à faire déborder. Refusée (429, 503), la requête
 * est rejouée après une courte pause, croissante ; au-delà de quatre essais,
 * l'erreur remonte.
 */
export async function avecPatience<T>(
  requete: () => Promise<T>,
  pause = (ms: number) => new Promise((r) => setTimeout(r, ms)),
): Promise<T> {
  for (let essai = 1; ; essai += 1) {
    try {
      return await requete();
    } catch (e) {
      const statut = e instanceof ApiError ? e.status : 0;
      if (essai >= 4 || (statut !== 429 && statut !== 503)) throw e;
      await pause(800 * essai);
    }
  }
}

export async function collecter(
  api: ClientApi,
  p: Parametres,
  avancer: (etape: string) => void,
): Promise<Section[]> {
  const lire = <T>(chemin: string) => avecPatience(() => api.requete<T>(chemin, { classe: p.classeId }));

  avancer('Préparation');
  const [periodesAnnee, toutesMatieres, programme] = await Promise.all([
    lire<Periode[]>(`/terms?schoolYearId=${p.annee.id}`),
    lire<Matiere[]>('/subjects'),
    lire<Programme>('/curriculum/tree'),
  ]);
  const periodes = p.periodeId ? periodesAnnee.filter((t) => t.id === p.periodeId) : periodesAnnee;
  const periode = periodes.length === 1 && p.periodeId ? periodes[0]! : null;
  const debut = periode?.startsOn ?? p.annee.startsOn;
  const fin = periode?.endsOn ?? p.annee.endsOn;
  const matieres = toutesMatieres.filter((m) => p.matieres.includes(m.name));
  const annee = p.annee.label;
  const libellePeriode = periode ? (periode.name || `Période ${periode.number}`) : '';

  const sections: Section[] = [];
  for (const cle of p.modules) {
    switch (cle) {
      case 'eleves': {
        avancer('Liste des élèves');
        sections.push(...sectionEleves(await lire<Eleve[]>(`/students?schoolYearId=${p.annee.id}`), annee));
        break;
      }
      case 'edt': {
        avancer('Emploi du temps');
        const filtre = periode ? `&termId=${periode.id}` : '';
        sections.push(...sectionEmploiDuTemps(
          await lire<Creneau[]>(`/timetable?schoolYearId=${p.annee.id}${filtre}`), libellePeriode, annee,
        ));
        break;
      }
      case 'programmation': {
        avancer('Programmation annuelle');
        // Toujours l'année entière : une programmation se lit sur ses cinq périodes.
        const blocs = (await parLots(periodesAnnee, (t) => lire<Bloc[]>(`/plan-blocks?termId=${t.id}`))).flat();
        sections.push(...sectionProgrammation(
          blocs, matieres, periodesAnnee, programme, (t) => lundisEntre(t.startsOn, t.endsOn).length, annee,
        ));
        break;
      }
      case 'progressions': {
        avancer('Progressions');
        const toutes = (await parLots(periodes, (t) => lire<Progression[]>(`/progressions?termId=${t.id}`))).flat();
        sections.push(...sectionProgressions(toutes, matieres, periodes, programme, annee));
        break;
      }
      case 'journal': {
        // Toute la période en UNE requête : semaine par semaine, quarante
        // requêtes dépassaient la limite de débit du proxy.
        avancer('Cahier journal');
        const seances = await lire<Seance[]>(`/lessons?from=${debut}&to=${fin}`);
        const dansLaPlage = seances.filter((s) => s.date >= debut && s.date <= fin);
        sections.push(...sectionJournal(dansLaPlage, toutesMatieres, p.unJourParPage, libellePeriode, annee));
        break;
      }
      case 'fiches': {
        avancer('Fiches de préparation');
        const dossiers = await parLots(p.sequences, (id) => lire<Dossier>(`/prep-sheets/sequences/${id}`));
        sections.push(...sectionFiches(dossiers));
        break;
      }
      case 'evaluation': {
        avancer('Évaluation');
        const noms = domainesDu(programme).filter((n) => p.matieres.includes(n));
        const grilles = await parLots(noms, async (nom) => ({
          matiere: nom,
          grille: await lire<GrilleEvaluation>(`/assessments?subject=${encodeURIComponent(nom)}`),
        }));
        sections.push(...sectionEvaluation(grilles, annee));
        break;
      }
      case 'heures': {
        avancer('108 heures');
        const [heures, objectifs] = await Promise.all([
          lire<{ activites: Activite[] }>(`/service-hours?schoolYearId=${p.annee.id}`),
          lire<Objectifs>('/service-hours/objectives'),
        ]);
        sections.push(...sectionHeures(heures.activites, objectifs, annee));
        break;
      }
    }
  }
  return sections;
}

export { lundisEntre };
